import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Typography,
    Paper,
    TableContainer,
    Tooltip,
    TablePagination,
    IconButton
} from '@mui/material';
import moment from 'moment';
import axiosInstance from '../../../lib/axios';
import CloseIcon from '@mui/icons-material/Close';

const TodayFollowUpPopup = ({ open, onClose, propId, selectedFollowBy }) => {
    const [todayRows, setTodayRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState(new Set())

    useEffect(() => {
        if (open && propId) {
            fetchTodayFollowUps();
        }
    }, [open, propId, selectedFollowBy]);

    const fetchTodayFollowUps = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post(
                `TrnFlwup/GetTrnFlwupDashBoard?currentPage=1&limit=15`,
                {
                    "PageNumber": 1,
                    "PageSize": 15,
                    "SearchText": "",
                    "PropId": propId,
                    "Flag": "today",
                    "FlwUserId": selectedFollowBy?.FlwUserId || ""
                }
            );

            if (response.data.Status === 0) {
                const formattedData = response?.data?.Data?.map(item => ({
                    ...item,
                    EnqDt: item.EnqDt ? moment(item.EnqDt).format('DD-MM-YYYY') : '',
                    NextFlwDt: item.NextFlwDt ? moment(item.NextFlwDt).format('DD-MM-YYYY') : '',
                    FlwDt: item.FlwDt ? moment(item.FlwDt).format('DD-MM-YYYY') : '',
                    LastFlwDt: item.LastFlwDt ? moment(item.LastFlwDt).format('DD-MM-YYYY') : '',
                    OtherRmk: item.OtherRmk,
                    VisitSt: item.VisitSt === 'Y' ? 'Yes' : 'No',
                    Status: item.flwStatus === "0" ? 'Cancel' :
                        item.flwStatus === "1" ? 'Open' :
                            item.flwStatus === "5" ? 'Close' :
                                item.flwStatus || 'Unknown'
                }));
                setTodayRows(formattedData);
            } else {
                setTodayRows([]);
            }
        } catch (error) {
            console.error('Error fetching today followups:', error);
            setTodayRows([]);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const columns = [
        { id: 'FlwEmpNm', label: 'Follow By' },
        { id: 'EnqNo', label: 'EnquiryNo.' },
        { id: 'EnqDt', label: 'EnqDate' },
        { id: 'LastFlwUserName', label: 'LastFollowBy' },
        { id: 'Status', label: 'Status' },
        { id: 'EnqTypeNm', label: 'EnqType' },
        { id: 'VisitSt', label: 'Visit' },
        { id: 'Remark', label: 'Remark' },
        { id: 'LastFlwDt', label: 'LastFollowUp' },
        { id: 'NextFlwDt', label: 'NextFollowUp' },
        { id: 'EnqBy', label: 'EnqBy' },
        { id: 'EnqFor', label: 'EnqFor' },
        { id: 'MobileNo', label: 'Mobile No.' },
        { id: 'Tenure', label: 'Tenure' },
        { id: 'Days', label: 'Days' },
        { id: 'SourceBy', label: 'SourceBy' },
        { id: 'SourceName', label: 'SourceName' },
        { id: 'VisitEmpNm', label: 'Visit With' },
        { id: 'VisitRemark', label: 'VisitRemark' },
        { id: 'OtherRmk', label: 'Remark' }
    ];

    const getCellStyle = (columnId, value) => {
        if (columnId === 'Status') {
            switch (value) {
                case 'Cancel': return { backgroundColor: '#F44336', color: 'white', borderRadius: '4px', textAlign: 'center' };
                case 'Close': return { backgroundColor: '#4CAF50', color: 'white', borderRadius: '4px', textAlign: 'center' };
                case 'Open': return { backgroundColor: '#2196F3', color: 'white', borderRadius: '4px', textAlign: 'center' };
                default: return { textAlign: 'center' };
            }
        }
        return {};
    };

    const handleRowDoubleClick = (row) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            newSet.add(row.EnqId);
            return newSet;
        });
        sessionStorage.setItem('followupFormData', JSON.stringify({
            EnqId: row.EnqId,
            FlwId: row.FlwId,
            rowData: row,
            mode: 'view'
        }));
        window.open('/FollowupForm', '_blank');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#635bff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    position: 'relative',
                    borderBottom: '1px solid #e0e0e0'
                }}
            >
                <Typography variant="h6" component="span">
                    Today's Follow Up List
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ padding: 0 }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                    </div>
                ) : todayRows.length > 0 ? (
                    <>
                        <TableContainer component={Paper} sx={{ maxHeight: 'calc(90vh - 180px)' }}>
                            <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse' }}>
                                <TableHead>
                                    <TableRow>
                                        {columns.map(column => (
                                            <TableCell
                                                key={column.id}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#dddbff',
                                                    whiteSpace: 'nowrap',
                                                    border: '1px solid #e0e0e0',
                                                    padding: '6px 12px'
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(rowsPerPage > 0
                                        ? todayRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        : todayRows
                                    ).map((row, index) => (
                                        <TableRow hover
                                            key={row.EnqId || index}
                                            onDoubleClick={() => handleRowDoubleClick(row)}

                                        >
                                            {columns.map(column => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell
                                                        key={column.id}
                                                        sx={{
                                                            padding: '0px 8px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            backgroundColor: '#ccffcc !important',
                                                            whiteSpace: 'nowrap',
                                                            border: '1px solid #e0e0e0',
                                                            cursor: 'pointer',
                                                            ...getCellStyle(column.id, value)
                                                        }}
                                                    >
                                                        <Tooltip title={value} arrow placement='top'
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        fontSize: '14px',
                                                                        maxWidth: '500px',
                                                                        cursor: 'pointer'
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <span>{value}</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 50, 100, 500]}
                            component="div"
                            count={todayRows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                backgroundColor: '#f5f5f5',
                                '& .MuiTablePagination-toolbar': {
                                    paddingLeft: '16px',
                                    justifyContent: 'flex-start'
                                }
                            }}
                            labelDisplayedRows={({ from, to, count }) => (
                                <>
                                    <Typography component="span" sx={{ fontWeight: 'bold', mr: 2 }}>
                                        Today Records: {count}
                                    </Typography>
                                    <Typography component="span">
                                        Showing {from}-{to} of {count}
                                    </Typography>
                                </>
                            )}
                        />
                    </>
                ) : (
                    <Typography variant="body1" align="center" sx={{ padding: '20px' }}>
                        No follow-ups scheduled for today
                    </Typography>
                )}
            </DialogContent>

        </Dialog>
    );
};

export default TodayFollowUpPopup;